import Alert from "@mui/material/Alert";
import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { TagList } from "@/shared/components/TagList";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks";
import { SessionRecord } from "@/shared/domain/sessions/SessionRecord";
import { useSessionTablePreferences } from "@/shared/domain/users/hooks";
import {
  composeFilters,
  DataTable,
  buildColumnsForPreferences,
  colId,
  createColumnHelper,
  useDataTableState,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type Filter,
  type FilterDefinition,
  type ICellRendererParams,
  type ValueFormatterParams,
  type ValueGetterParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation/Goto";
import { formatNanoDuration, formatNanoTimestamp } from "@/shared/time";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./SessionsList.module.css";

const columnHelper = createColumnHelper<SessionRecord>();

const durationColumn: ColDef<SessionRecord, bigint | null> = {
  colId: "duration",
  headerName: "Duration",
  flex: 1,
  sortable: true,
  cellClass: styles.monoCell,
  valueGetter: (params: ValueGetterParams<SessionRecord>) => {
    const row = params.data;
    if (
      !row ||
      row.max_timestamp_ns === null ||
      row.min_timestamp_ns === null
    ) {
      return null;
    }
    return row.max_timestamp_ns - row.min_timestamp_ns;
  },
  valueFormatter: (
    params: ValueFormatterParams<SessionRecord, bigint | null>,
  ) => formatNanoDuration(params.value),
};

const STATIC_SESSION_COLUMNS: ColDef<SessionRecord>[] = [
  columnHelper.accessor("session_id", {
    headerName: "ID",
    flex: 1,
    cellClass: styles.monoCell,
    cellRenderer: (params: ICellRendererParams<SessionRecord, string>) => {
      const id = params.value;
      if (!id) {
        return null;
      }
      return (
        <Link to={`${Goto.route.Sessions}/${id}`} className={styles.idLink}>
          {id}
        </Link>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 1,
    sortable: true,
    valueFormatter: (params) => params.value ?? "Untitled session",
  }),
  columnHelper.accessor("min_timestamp_ns", {
    headerName: "Start",
    flex: 1,
    sortable: true,
    cellClass: styles.monoCell,
    valueFormatter: (params) => formatNanoTimestamp(params.value),
  }),
  // Sort disabled: backend RoboQL catalog marks `max_timestamp_ns` non-sortable.
  // Re-enable once ENG-2522 enables it up server-side.
  columnHelper.accessor("max_timestamp_ns", {
    headerName: "End",
    flex: 1,
    sortable: false,
    cellClass: styles.monoCell,
    valueFormatter: (params) => formatNanoTimestamp(params.value),
  }),
  durationColumn,
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    flex: 1,
    sortable: false,
    cellRenderer: (params: ICellRendererParams<SessionRecord, string>) => (
      <RobotoPrincipalUnderlineLink principal={params.value ?? ""} icon />
    ),
  }),
  // Sort omitted: backend RoboQL `SESSION_FIELDS` catalog registers
  // `tags` with `is_sortable=False`; including `sortable: true` would
  // surface a click target that always 400s.
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    cellStyle: { display: "flex", alignItems: "center" },
    // String projection for AG Grid's clipboard export, quick filter,
    // and screen-reader announcements; the visible cell still renders
    // through `cellRenderer` below.
    valueFormatter: (params: ValueFormatterParams<SessionRecord, string[]>) =>
      (params.value ?? []).join(", "),
    cellRenderer: (params: ICellRendererParams<SessionRecord, string[]>) => (
      <TagList tags={params.value ?? []} maxDisplayCount={3} />
    ),
  }),
];

const STATIC_SESSION_PICKER_OPTIONS: ColumnPickerOption[] =
  STATIC_SESSION_COLUMNS.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

export interface SessionsListProps {
  /**
   * Applied to the outer container so the parent can size or position the list per page context.
   */
  className?: classNames.Argument;

  /**
   * Filters joined under an outer AND with the user's filter group, so
   * the user cannot widen results beyond them (e.g. the dataset tab pins
   * `dataset.dataset_id EQUALS <id>`). Hidden from the chip bar.
   *
   * The outer wrap is AND even when the user picks OR match mode; the
   * user's group nests inside so OR cannot dissolve the scope boundary.
   */
  scope?: Filter[];

  /**
   * Filterable fields the user can pick from. Omit to hide the filter
   * button and chip bar; the column-visibility picker and table still
   * render.
   */
  filterDefinitions?: FilterDefinition[];

  /**
   * Empty-state copy. Customize per-mount so dataset and device tabs can
   * scope the message (e.g. "No sessions for this dataset").
   */
  noRowsContent?: React.ReactNode;
}

/**
 * Paginated, filterable, sortable table of sessions. Submits a structured
 * query for the caller's org to obtain a `query_id`, then fetches a page
 * and total count off that id. Column order and page size persist per-user
 * via the shared session table preferences; sort is session-local.
 *
 * User filters always nest inside an outer AND with `scope`, so the
 * user's `matchMode` only governs how their own filters relate to each
 * other. See `composeFilters` for the composition rule.
 *
 * Renders no chrome of its own. Consumers wrap it in `<PageLayout>` or
 * mount it inside an outlet so the same component fits either context.
 */
export function SessionsList({
  className,
  scope,
  filterDefinitions,
  noRowsContent = "No sessions",
}: SessionsListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useSessionTablePreferences();

  // Stored prefs are the source of truth; don't union with defaults on
  // mount — it would re-add columns the user explicitly removed.
  const columns = React.useMemo<ColDef<SessionRecord>[]>(
    () =>
      buildColumnsForPreferences<SessionRecord>({
        staticColumns: STATIC_SESSION_COLUMNS,
        dynamicColumns: [],
        preferenceOrder: preferences.columns,
      }),
    [preferences.columns],
  );

  const tableState = useDataTableState<SessionRecord>({
    getRowId: (row) => row.session_id,
    initialSort: { columnId: "min_timestamp_ns", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });

  // Wrap user filters under an outer AND with `scope` so a dataset or
  // device tab can pin the list to its entity (passed through `scope`)
  // without exposing those predicates as user-editable chips. The user's
  // `matchMode` only governs how their own filters relate to each other;
  // an `OR` selection cannot dissolve the scope boundary. See
  // `composeFilters` and the `scope` prop's contract.
  const queryCondition = React.useMemo(
    () => composeFilters(scope ?? [], tableState.filters, tableState.matchMode),
    [scope, tableState.filters, tableState.matchMode],
  );

  // Omit the sort fields when the user has cleared the sort so the
  // server falls back to its own default ordering rather than receiving
  // an explicit "no sort" directive.
  const query: SearchQueryBody = React.useMemo(
    () => ({
      ...(queryCondition && { condition: queryCondition }),
      ...(tableState.sort && {
        sort_by: tableState.sort.columnId,
        sort_direction:
          tableState.sort.direction.toUpperCase() as SortDirection,
      }),
    }),
    [queryCondition, tableState.sort],
  );

  const queryKey = sessionQueryKeys.list(resolvedOrgId, query);

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Sessions,
    contentMode: QueryContentMode.RecordOnly,
    queryKey,
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<SessionRecord>(
    queryId,
    tableState.currentPage,
    tableState.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  // Prefer the submit error: the page fetch is disabled until submit
  // returns a `query_id`, so any error it surfaces is downstream of an
  // already-surfaced submit failure.
  const fetchError = submitQuery.error ?? pageQuery.error;

  return (
    <div className={classNames(styles.container, className)}>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <DataTable.Root>
        <DataTable.Actions>
          {filterDefinitions?.length ? (
            <DataTable.Filters
              filterDefinitions={filterDefinitions}
              onFilterSelected={tableState.addFilter}
            />
          ) : null}
          <DataTable.ColumnVisibility
            options={STATIC_SESSION_PICKER_OPTIONS}
            visibleColumnIds={tableState.visibleColumnIds}
            setColumnVisibility={tableState.setColumnVisibility}
          />
        </DataTable.Actions>
        {filterDefinitions?.length ? (
          <DataTable.ActiveFilters
            {...tableState.activeFilters}
            filterDefinitions={filterDefinitions}
          />
        ) : null}
        <DataTable.Table<SessionRecord>
          {...tableState.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          getRowId={(row) => row.session_id}
          noRowsContent={noRowsContent}
          totalRowCount={countQuery.data}
        />
      </DataTable.Root>
    </div>
  );
}
